import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea, ReferenceLine } from 'recharts';
import PrintLayout from '../PrintLayout';
import { compressTimeGaps, isInquiry, countTagOccurrences } from '../../../utils/metrics';
import { extractTaggedTexts } from '../../../utils/shortcodes';

const WINDOW_MS = 2 * 60 * 1000;
const BAND_COLORS = ['#2a3d5b12', '#5f794212', '#cba43a12', '#922b3f12', '#7209b712', '#4cc9f012'];

function BandLabel({ viewBox, value }) {
  if (!value || !viewBox) return null;
  const { x, width, y } = viewBox;
  if (width < 24) return null;
  const label = value.length > 14 ? value.slice(0, 13) + '…' : value;
  return <text x={x + width / 2} y={y + 10} textAnchor="middle" fontSize={7.5} fill="#555">{label}</text>;
}

function buildTimeline(entries) {
  const sorted = compressTimeGaps(entries)
    .filter(e => e.type !== 'break' && e.startTime)
    .sort((a, b) => a.startTime - b.startTime);

  let lineData = [];
  let peakSpeakers = 0;
  if (sorted.length > 0) {
    const start = sorted[0].startTime;
    const end = sorted[sorted.length - 1].endTime || sorted[sorted.length - 1].startTime;
    const numBuckets = Math.max(Math.ceil((end - start) / WINDOW_MS), 1);

    for (let b = 0; b < numBuckets; b++) {
      const bucketStart = start + b * WINDOW_MS;
      const bucketEnd = bucketStart + WINDOW_MS;
      const bucketMid = bucketStart + WINDOW_MS / 2;
      const bucket = { time: `${Math.round((b * WINDOW_MS) / 60000)}m` };

      let activeTopic = null;
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].startTime <= bucketMid) {
          if (sorted[i].topic) { activeTopic = sorted[i].topic; break; }
          if (sorted[i].section) { activeTopic = sorted[i].section; break; }
        }
      }
      if (activeTopic) bucket._topic = activeTopic;

      const speakersInWindow = new Set();
      let questionsInWindow = 0;
      sorted.forEach(e => {
        if (e.startTime >= bucketStart && e.startTime < bucketEnd) {
          speakersInWindow.add(e.speakerDisplayName || e.speakerName?.split('@')[0] || 'Unknown');
          if (isInquiry(e)) questionsInWindow++;
        }
      });
      bucket.uniqueSpeakers = speakersInWindow.size;
      bucket.questionsInWindow = questionsInWindow;
      peakSpeakers = Math.max(peakSpeakers, bucket.uniqueSpeakers);
      lineData.push(bucket);
    }
  }

  const kmTimes = [];
  if (sorted.length > 0) {
    const start = sorted[0].startTime;
    sorted.forEach(e => {
      if (e.text?.includes('/km') && e.startTime) {
        const bucketIdx = Math.min(Math.floor((e.startTime - start) / WINDOW_MS), lineData.length - 1);
        const label = lineData[bucketIdx]?.time;
        if (label && !kmTimes.includes(label)) kmTimes.push(label);
      }
    });
  }

  const topicBands = [];
  if (lineData.length > 0) {
    let currentLabel = null;
    let bandStartIdx = 0;
    lineData.forEach((d, i) => {
      const label = d._topic || null;
      if (label !== currentLabel) {
        if (currentLabel !== null) {
          topicBands.push({ label: currentLabel, x1: lineData[bandStartIdx].time, x2: lineData[i - 1].time, colorIdx: topicBands.length });
        }
        currentLabel = label;
        bandStartIdx = i;
      }
      if (i === lineData.length - 1 && currentLabel !== null) {
        topicBands.push({ label: currentLabel, x1: lineData[bandStartIdx].time, x2: d.time, colorIdx: topicBands.length });
      }
    });
  }

  return { lineData, topicBands, kmTimes, peakSpeakers, sorted };
}

function NumberTile({ label, value }) {
  return (
    <div style={{ flex: 1, background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '0.8px', color: '#6b757e', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#1e2e45', marginTop: 4 }}>{value}</div>
    </div>
  );
}

function RankedListTile({ label, items, renderName, renderValue }) {
  return (
    <div style={{ flex: 1.3, background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '0.8px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
      {items.length === 0
        ? <div style={{ fontSize: 8.5, color: '#9aa2ab', fontStyle: 'italic' }}>—</div>
        : items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#9aa2ab', minWidth: 10 }}>{i + 1}.</span>
            <span style={{ fontSize: 9, fontWeight: 600, color: '#1e2e45', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{renderName(item)}</span>
            <span style={{ fontSize: 8.5, fontWeight: 600, color: '#5f7942', whiteSpace: 'nowrap' }}>{renderValue(item)}</span>
          </div>
        ))}
    </div>
  );
}

export default function PrintTimelineV2({ pageNumber, reportData, entries = [], meeting }) {
  const { lineData, topicBands, kmTimes, sorted } = buildTimeline(entries);

  const askedForHelp = countTagOccurrences(entries, ['/h']);
  const askedForInput = countTagOccurrences(entries, ['/i']);
  const admitsMistake = countTagOccurrences(entries, ['/e']);
  const levity = countTagOccurrences(entries, ['/l']);
  const admitsWeakness = countTagOccurrences(entries, ['/w']);

  const displayNameMap = {};
  entries.forEach(e => {
    if (e.speakerName) displayNameMap[e.speakerName] = e.speakerDisplayName || e.speakerName.split('@')[0];
  });
  const dn = (name) => displayNameMap[name] || name?.split('@')[0] || name;
  const pairDn = (pair) => pair?.split(' ↔ ').map(dn).join(' ↔ ') || '—';
  const msToTime = (ms) => {
    if (!ms) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
  };

  const awards = reportData?.awards || {};
  const longestComments = awards.longestComments || [];
  const longestMutes = awards.longestMutes || [];
  const longestExchanges = awards.longestExchanges || [];

  // Consultants can refine a /km marker's one-sentence summary on the post-meeting
  // debrief form (MeetingInventory.js's keyMomentNotes, keyed by entry id) — that
  // edited version wins over the raw live-typed text when present.
  const keyMoments = [];
  if (sorted.length > 0) {
    const start = sorted[0].startTime;
    const notes = meeting?.keyMomentNotes || {};
    sorted.forEach(e => {
      if (!e.text?.includes('/km') || !e.startTime) return;
      const text = notes[e.id]?.trim() || extractTaggedTexts(e.text, '/km').join(' ');
      if (!text) return;
      const offsetMs = e.startTime - start;
      const mm = Math.floor(offsetMs / 60000);
      const ss = Math.floor((offsetMs % 60000) / 1000);
      keyMoments.push({ time: `${mm}:${String(ss).padStart(2, '0')}`, text });
    });
  }

  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, display: 'flex' }}>
        <div style={{ width: '32%', background: '#1e2e45', padding: '6% 5%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '2px', color: '#5f7942', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>
            Section 1: Compelling Content
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>
            Great meetings need productive drama.
          </div>
          <div style={{ width: 36, height: 2, background: '#5f7942', marginBottom: 14, borderRadius: 1 }} />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: 14 }}>
            During great meetings, the most important issues are discussed. That's easier said than done, as our agendas get filled with a variety of topics that don't require debate, decision, or both. We challenge leaders to validate their agenda in real time and make sure the right topics have the right space in the meeting.
          </div>
          <div style={{ padding: '10px 12px', borderLeft: '2px solid #5f7942', fontSize: 10, fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
            "Meetings are boring because they lack drama or conflict. This is a shame because most meetings have plenty of potential for drama, which is essential for keeping human beings engaged."
            <div style={{ marginTop: 6, fontStyle: 'normal', fontWeight: 600, color: 'rgba(255,255,255,0.3)' }}>
              — Patrick Lencioni, <i>Death by Meeting</i>
            </div>
          </div>
        </div>

        <div style={{ width: '68%', background: '#f5f4f0', padding: '4% 4.5%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>
            Speakers &amp; questions, over the meeting
          </div>
          <div style={{ background: 'white', border: '1px solid rgba(42,61,91,0.1)', borderRadius: 10, padding: '8px 14px', marginBottom: 12 }}>
            {lineData.length > 1 ? (
              <ResponsiveContainer width="100%" height={235}>
                <AreaChart data={lineData} margin={{ left: -10 }}>
                  <defs>
                    <linearGradient id="v2SpeakersGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2a3d5b" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#2a3d5b" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="v2QuestionsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#cba43a" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#cba43a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0ddd8" vertical={false} />
                  <XAxis dataKey="time" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} allowDecimals={false} domain={[0, 'auto']} />
                  <Tooltip formatter={(val, name) => [val, name === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked']} labelFormatter={(label) => `At ${label}`} />
                  {topicBands.map((band, i) => (
                    <ReferenceArea key={i} x1={band.x1} x2={band.x2} fill={BAND_COLORS[band.colorIdx % BAND_COLORS.length]} label={<BandLabel value={band.label} />} />
                  ))}
                  {kmTimes.map((t, i) => (
                    <ReferenceLine key={`km-${i}`} x={t} stroke="#4361ee" strokeWidth={1.5} strokeDasharray="4 3" label={{ value: '★', position: 'insideTop', fill: '#4361ee', fontSize: 10 }} />
                  ))}
                  <Area type="monotone" dataKey="uniqueSpeakers" stroke="#2a3d5b" strokeWidth={2.5} fill="url(#v2SpeakersGrad)" dot={false} isAnimationActive={false} />
                  <Area type="monotone" dataKey="questionsInWindow" stroke="#cba43a" strokeWidth={1.5} fill="url(#v2QuestionsGrad)" dot={false} isAnimationActive={false} />
                  <Legend content={(props) => {
                    const items = (props.payload || []).map(p => ({ color: p.color, label: p.dataKey === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked' }));
                    return (
                      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', fontSize: 9, paddingTop: 4 }}>
                        {items.map((item, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ display: 'inline-block', width: 12, height: 3, background: item.color, borderRadius: 2 }} />
                            {item.label}
                          </span>
                        ))}
                      </div>
                    );
                  }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ fontSize: 10, color: '#6b757e', fontStyle: 'italic', padding: 20 }}>Not enough data to display chart.</div>
            )}
          </div>

          {keyMoments.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '0.8px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 4 }}>Key Moments</div>
              {keyMoments.slice(0, 3).map((km, i) => (
                <div key={i} style={{ fontSize: 9, color: '#2a3d5b', lineHeight: 1.5, marginBottom: 2 }}>
                  <span style={{ color: '#5f7942', fontWeight: 700 }}>{km.time}</span> {km.text}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <NumberTile label="Requests for Help" value={askedForHelp} />
            <NumberTile label="Requests for Input" value={askedForInput} />
            <NumberTile label="Admits Mistake" value={admitsMistake} />
            <NumberTile label="Levity" value={levity} />
            <NumberTile label="Admits Weakness" value={admitsWeakness} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <RankedListTile
              label="Longest Comments"
              items={longestComments}
              renderName={(item) => dn(item.speaker)}
              renderValue={(item) => msToTime(item.ms)}
            />
            <RankedListTile
              label="Longest on Mute"
              items={longestMutes}
              renderName={(item) => dn(item.speaker)}
              renderValue={(item) => msToTime(item.ms)}
            />
            <RankedListTile
              label="Most Back-and-Forth"
              items={longestExchanges}
              renderName={(item) => pairDn(item.pair)}
              renderValue={(item) => msToTime(item.ms)}
            />
          </div>
        </div>
      </div>
    </PrintLayout>
  );
}
